import type { ProductFacetTypeKey } from "@/types/product-facet";

export const DOCUMENTATION_TEMPLATE_KINDS = [
  "Product",
  "Function",
  "JTBD",
  "Experience",
  "Capability",
  "Feature",
  /** Trailing Feature Documentation section for coding agents — generate on ask only. */
  "FeatureExecutionBrief",
  "Project",
  "Phase",
  "Deliverable",
  "WorkPackage",
  "WorkItem",
] as const;

export type DocumentationTemplateKind =
  (typeof DOCUMENTATION_TEMPLATE_KINDS)[number];

export interface DocumentationTemplate {
  readonly kind: DocumentationTemplateKind;
  readonly label: string;
  readonly headings: readonly string[];
  readonly outline: string;
}

export const DOCUMENTATION_GENERATE_AGENT_HANDLE_FACET =
  "kriyaProductGraphAgent";
export const DOCUMENTATION_GENERATE_AGENT_HANDLE_WORK =
  "kriyaWorkBreakdownAgent";
export const DOCUMENTATION_GENERATE_RESOURCE_ID = "documentation-generate";

const PRODUCT_VISION_OUTLINE = `# Vision
What this product is, in two or three sentences.

# Who it's for
Primary users or customer, not an org chart.

# Problem
The pain this product exists to remove.

# Principles
Hard rules for what we will and will not optimize for.

# Out of scope
What this product is not, even if requested often.`;

const FUNCTION_BRIEF_OUTLINE = `# Domain
What this product domain owns.

# Users
Who works in this domain (roles, not named people).

# Outcomes
Jobs or results this domain is responsible for. Names only; detail lives on JTBDs.

# Boundaries
What belongs in sibling Functions instead.`;

const JTBD_PRFAQ_OUTLINE = `# Job
The job statement: When [situation], I want to [motivation], so I can [outcome].

# Audience
Who is hiring the product for this job.

# Press release
One short announcement as if the job were already solved. Include a headline and a paragraph a customer would believe.

# FAQ
- Why this job matters now
- What we will not do
- How we know it is done
- What alternatives exist today`;

const EXPERIENCE_JOURNEY_OUTLINE = `# Journey
The end-to-end experience in one paragraph.

# Entry and exit
How the user starts, and what “done” looks like.

# Steps
Numbered stages in the flow. Not UI widgets.

# Failure modes
Where the journey breaks and what the user should see.`;

const CAPABILITY_BRIEF_OUTLINE = `# Ability
The reusable ability this capability represents.

# What it enables
Experiences or outcomes that depend on it.

# Constraints
Rules, compliance, or platform limits.

# Features
Child Feature names only. Each Feature has its own PRD.`;

const FEATURE_PRD_HEADINGS_OUTLINE = `# Problem
Why this Feature exists. One paragraph.

# User
Who uses it, in this Experience.

# Ability
The “Ability to…” outcome. Must match Description.

# Requirements
Bullet list of what must be true. User-visible behavior, not a tech design.

# Acceptance criteria
Testable checks. Given / when / then or short bullets.

# Evidence
Code paths, surfaces, or PRs if known. Empty list if none.

# Out of scope
What this Feature will not do (belongs to a sibling Feature or later).`;

/** Trailing Feature Documentation section for coding agents. */
const FEATURE_EXECUTION_BRIEF_OUTLINE = `# Execution summary
One paragraph a coding agent can act on. Ground on the PRD above; do not restate the full Problem/User.

# Implementation checks
Testable checks for this implementation pass. Prefer pointing at Acceptance criteria above when unchanged; add path- or slice-specific checks here.

# Repo paths
Known code paths or surfaces. Empty list if none.

# Suggested slices
1–5 implementation slices (title + one line each). Not a full WBS.`;

/** Full Feature Documentation: PRD, then a horizontal rule, then execution brief. */
const FEATURE_DOCUMENTATION_OUTLINE = `${FEATURE_PRD_HEADINGS_OUTLINE}


---


${FEATURE_EXECUTION_BRIEF_OUTLINE}`;

/** Blank line, rule, blank line — keeps PRD and execution brief visually separated. */
const FEATURE_SECTION_SEPARATOR = "\n\n\n---\n\n\n";

const PROJECT_CHARTER_OUTLINE = `# Objective
What this project will have achieved when it is done.

# In scope
Narrative of what we are delivering. Do not contradict JsonData.spec.inScope.

# Out of scope
What we are explicitly not doing.

# Success criteria
How we will know the project succeeded.

# Assumptions
What must remain true for this plan to hold.

# Approach
How we will sequence the work at a Phase level. No Work Item lists.`;

const PHASE_EXIT_OUTLINE = `# Purpose
Why this phase exists in the project.

# Exit criteria
What must be true before the next phase starts.`;

const DELIVERABLE_BRIEF_OUTLINE = `# Intent
The outcome this deliverable produces.

# Constraints
Limits on how it may be produced.

# Acceptance criteria
How we will accept the deliverable.

# Linked product outcomes
Linked JTBD / Feature names and ids. Do not paste their PRDs.`;

const WORK_PACKAGE_OUTLINE = `# Outcome
The noun this package produces.

# Approach
How the Work Items underneath get us there. Names of items, not their steps.`;

const WORK_ITEM_OUTLINE = `# Outcome
The tangible output of this one step.

# Context
Pointers only: parent Deliverable, linked Feature, or “see Feature X PRD”. Do not paste parent docs.

# Steps
Ordered actions for the assignee (human or agent).

# Definition of done
How we will know this item is complete. Align with completionCriteria.`;

function template(
  kind: DocumentationTemplateKind,
  label: string,
  outline: string,
): DocumentationTemplate {
  return {
    kind,
    label,
    headings: extractMarkdownH1s(outline),
    outline,
  };
}

export const DOCUMENTATION_TEMPLATES: Record<
  DocumentationTemplateKind,
  DocumentationTemplate
> = {
  Product: template("Product", "Product vision", PRODUCT_VISION_OUTLINE),
  Function: template("Function", "Function brief", FUNCTION_BRIEF_OUTLINE),
  JTBD: template("JTBD", "JTBD PRFAQ", JTBD_PRFAQ_OUTLINE),
  Experience: template(
    "Experience",
    "Experience journey",
    EXPERIENCE_JOURNEY_OUTLINE,
  ),
  Capability: template(
    "Capability",
    "Capability brief",
    CAPABILITY_BRIEF_OUTLINE,
  ),
  Feature: template("Feature", "Feature documentation", FEATURE_DOCUMENTATION_OUTLINE),
  FeatureExecutionBrief: template(
    "FeatureExecutionBrief",
    "Execution brief",
    FEATURE_EXECUTION_BRIEF_OUTLINE,
  ),
  Project: template("Project", "Project charter", PROJECT_CHARTER_OUTLINE),
  Phase: template("Phase", "Phase exit criteria", PHASE_EXIT_OUTLINE),
  Deliverable: template(
    "Deliverable",
    "Deliverable brief",
    DELIVERABLE_BRIEF_OUTLINE,
  ),
  WorkPackage: template(
    "WorkPackage",
    "Work package approach",
    WORK_PACKAGE_OUTLINE,
  ),
  WorkItem: template("WorkItem", "Work item card", WORK_ITEM_OUTLINE),
};

export function isDocumentationTemplateKind(
  value: string,
): value is DocumentationTemplateKind {
  return (DOCUMENTATION_TEMPLATE_KINDS as readonly string[]).includes(value);
}

export function documentationKindFromFacetTypeKey(
  typeKey: ProductFacetTypeKey | null | undefined,
): DocumentationTemplateKind | null {
  if (!typeKey) return null;
  return typeKey;
}

export function documentationKindFromWorkTypeName(
  name: string | null | undefined,
): DocumentationTemplateKind | null {
  const normalized = (name ?? "").trim().toLowerCase();
  if (!normalized) return null;
  if (normalized.includes("project")) return "Project";
  if (normalized.includes("phase")) return "Phase";
  if (normalized.includes("deliver")) return "Deliverable";
  if (
    normalized.includes("sub-task") ||
    normalized.includes("subtask") ||
    normalized.includes("sub task")
  ) {
    return "WorkItem";
  }
  if (
    normalized.includes("package") ||
    normalized.includes("pack") ||
    normalized.includes("task")
  ) {
    return "WorkPackage";
  }
  if (normalized.includes("item") || normalized.includes("story")) {
    return "WorkItem";
  }
  return null;
}

/** H1s that mark the Feature Documentation execution-brief section. */
export const FEATURE_EXECUTION_BRIEF_HEADINGS: readonly string[] = [
  "Execution summary",
  "Implementation checks",
  "Repo paths",
  "Suggested slices",
];

export const FEATURE_PRD_HEADINGS: readonly string[] = [
  "Problem",
  "User",
  "Ability",
  "Requirements",
  "Acceptance criteria",
  "Evidence",
  "Out of scope",
];

export type FeatureDocumentationGenerateMode =
  | "full"
  | "executionBriefOnly";

/**
 * Empty / incomplete PRD → full Feature doc.
 * PRD present but execution brief missing → append execution brief only.
 * Complete → full regenerate (optional instructions).
 */
export function resolveFeatureDocumentationGenerateMode(
  documentation: string | null | undefined,
): FeatureDocumentationGenerateMode {
  const markdown = documentation?.trim() ?? "";
  if (!markdown) return "full";

  const found = new Set(
    extractMarkdownH1s(markdown).map((heading) => normalizeHeading(heading)),
  );
  const prdComplete = FEATURE_PRD_HEADINGS.every((heading) =>
    found.has(normalizeHeading(heading)),
  );
  const executionComplete = FEATURE_EXECUTION_BRIEF_HEADINGS.every((heading) =>
    found.has(normalizeHeading(heading)),
  );

  if (prdComplete && !executionComplete) return "executionBriefOnly";
  return "full";
}

function isExecutionBriefHeading(heading: string): boolean {
  const normalized = normalizeHeading(heading);
  return FEATURE_EXECUTION_BRIEF_HEADINGS.some(
    (h) => normalizeHeading(h) === normalized,
  );
}

/**
 * Drop any existing Execution brief H1 block so a regenerate can replace it.
 * Keeps PRD (and any other content) before the first execution-brief H1.
 * Also drops a trailing `---` separator that precedes the brief.
 */
export function stripFeatureExecutionBrief(markdown: string): string {
  const lines = markdown.split("\n");
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index]?.trim() ?? "";
    if (!line.startsWith("# ")) continue;
    const heading = line.slice(2).trim();
    if (isExecutionBriefHeading(heading)) {
      let end = index;
      while (end > 0 && (lines[end - 1]?.trim() ?? "") === "") {
        end -= 1;
      }
      if (end > 0 && (lines[end - 1]?.trim() ?? "") === "---") {
        end -= 1;
        while (end > 0 && (lines[end - 1]?.trim() ?? "") === "") {
          end -= 1;
        }
      }
      return lines.slice(0, end).join("\n").trimEnd();
    }
  }
  return markdown.trimEnd();
}

/** Append or replace the Feature execution-brief section after the PRD body. */
export function mergeFeatureExecutionBrief(
  existingDocumentation: string | null | undefined,
  executionBriefMarkdown: string,
): string {
  const brief = executionBriefMarkdown.trim().replace(/^---\s*\n+/, "");
  const retained = stripFeatureExecutionBrief(existingDocumentation ?? "");
  if (!retained) return `${brief}\n`;
  return `${retained}${FEATURE_SECTION_SEPARATOR}${brief}\n`;
}

export function generateDocumentationButtonLabel(
  kind: DocumentationTemplateKind,
  hasExisting: boolean,
  documentation?: string | null,
): string {
  if (kind === "Feature") {
    const mode = resolveFeatureDocumentationGenerateMode(documentation);
    if (mode === "executionBriefOnly") {
      return "Generate execution brief";
    }
    return hasExisting
      ? "Regenerate Feature documentation"
      : "Generate Feature documentation";
  }
  const label = DOCUMENTATION_TEMPLATES[kind].label;
  return hasExisting ? `Regenerate ${label}` : `Generate ${label}`;
}

export function extractMarkdownH1s(markdown: string): string[] {
  const headings: string[] = [];
  for (const rawLine of markdown.split("\n")) {
    const line = rawLine.trim();
    if (!line.startsWith("# ")) continue;
    const heading = line.slice(2).trim();
    if (heading) headings.push(heading);
  }
  return headings;
}

function normalizeHeading(heading: string): string {
  return heading.trim().toLowerCase().replace(/['’]/g, "'");
}

export function documentationMissingHeadings(
  markdown: string,
  kind: DocumentationTemplateKind,
): string[] {
  const found = new Set(
    extractMarkdownH1s(markdown).map((heading) => normalizeHeading(heading)),
  );
  return DOCUMENTATION_TEMPLATES[kind].headings.filter(
    (heading) => !found.has(normalizeHeading(heading)),
  );
}

export interface DocumentationGenerateContext {
  readonly name: string | null;
  readonly description: string | null;
  readonly documentation: string | null;
  readonly jsonData: string | null;
  readonly parent: {
    readonly name: string | null;
    readonly description: string | null;
  } | null;
  /** Regeneration-only guidance from the user. */
  readonly instructions?: string | null;
}

export function buildDocumentationGenerateSystemPrompt(
  kind: DocumentationTemplateKind,
): string {
  const templateDef = DOCUMENTATION_TEMPLATES[kind];
  const executionBriefRules =
    kind === "FeatureExecutionBrief"
      ? `
11. This is only the Execution brief section of Feature Documentation. Emit ONLY these H1s.
12. Do not emit Problem, User, Ability, Requirements, Acceptance criteria, Evidence, or Out of scope.
13. Use existing Documentation (PRD) as grounding. Prefer pointing at Acceptance criteria above instead of duplicating them.
14. Suggested slices are implementation cuts for a coding agent — not a project WBS.
15. Do not emit a markdown horizontal rule (\`---\`); the app inserts one before this section.`
      : kind === "Feature"
        ? `
11. After "# Out of scope", leave a blank line, emit a markdown horizontal rule on its own line (\`---\`), leave another blank line, then the Execution brief H1s.
12. Do not invent other separators or extra H1s.`
        : "";

  return `You write the Documentation column for a Kriyastream ${templateDef.label}.

Rules:
1. Emit markdown only inside the JSON "documentation" string.
2. Use exactly these H1s, in this order: ${templateDef.headings.map((h) => `"${h}"`).join(", ")}.
3. Fill every heading. A short paragraph or list is enough. Use TBD if unknown.
4. Do not invent extra H1s. Do not use H2s as a substitute for required H1s.
5. Do not paste JsonData into the body. Ground on name, description, parent, and JsonData facts.
6. If Documentation and JsonData.spec disagree, follow spec for structure and write prose that does not contradict it.
7. Children point up (name the parent); they do not copy the parent document.
8. If existing Documentation is provided, keep true facts unless they contradict spec. Rewrite into this outline.
9. If regenerationInstructions are provided, treat them as required steering for this rewrite. Prefer them over style defaults when they conflict, without breaking rules 1–4.
10. Grain: a few hundred words. No filler.${executionBriefRules}

Template:
${templateDef.outline}

Return JSON only:
{ "documentation": "<markdown>" }`;
}

export function buildDocumentationGenerateUserMessage(
  kind: DocumentationTemplateKind,
  context: DocumentationGenerateContext,
): string {
  const instructions = context.instructions?.trim() || null;
  return JSON.stringify(
    {
      templateKind: kind,
      templateLabel: DOCUMENTATION_TEMPLATES[kind].label,
      name: context.name,
      description: context.description,
      jsonData: context.jsonData,
      parent: context.parent,
      existingDocumentation: context.documentation?.trim() || null,
      regenerationInstructions: instructions,
    },
    null,
    2,
  );
}
